import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api, { getAll } from '../../services/api';
import { notify } from '../../utils/notifications';
import authStore from '../../store/authStore';
import { puoModificare } from '../../utils/permessi';
import { euro, dataIT, dataOraIT } from './Eventi';
import {
  Search, CheckCircle, Clock, XCircle, Ban, Euro, X, Mail,
  CalendarPlus, Phone, AlertTriangle, Ticket, Trash2,
} from 'lucide-react';

export const STATI_ORDINE = {
  IN_SOSPESO: { label: 'Da incassare', classe: 'bg-amber-100 text-amber-800', icona: Clock },
  CONFERMATO: { label: 'Incassato', classe: 'bg-green-100 text-green-700', icona: CheckCircle },
  SCADUTO: { label: 'Scaduto', classe: 'bg-gray-100 text-gray-600', icona: XCircle },
  ANNULLATO: { label: 'Annullato', classe: 'bg-red-100 text-red-700', icona: Ban },
};

const OrdiniEventi = () => {
  const { utente } = authStore();
  const modificabile = puoModificare(utente);

  const [ordini, setOrdini] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [ricerca, setRicerca] = useState('');
  const [filtroStato, setFiltroStato] = useState('');
  const [filtroEvento, setFiltroEvento] = useState('');
  const [aperto, setAperto] = useState(null);

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      setOrdini(await getAll('/navette/ordini/'));
    } catch (errore) {
      console.error('Errore caricamento ordini:', errore);
      notify.error('Errore nel caricamento degli ordini');
    } finally {
      setCaricamento(false);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  /* Gli eventi su cui c'e' almeno un ordine, i piu' vicini per primi.
     Si ricavano dagli ordini invece di chiederli all'API: l'elenco e'
     gia' in memoria, e un evento senza ordini nel filtro non servirebbe
     a niente. */
  const eventi = Array.from(
    new Map(ordini.map((o) => [o.evento, {
      id: o.evento, titolo: o.evento_titolo, data: o.evento_data,
    }])).values()
  ).sort((a, b) => String(a.data).localeCompare(String(b.data)));

  // Il filtro per evento vale anche per i contatori in cima: "incassato"
  // mentre si guarda un evento solo deve essere l'incasso di quello.
  const perEvento = filtroEvento
    ? ordini.filter((o) => String(o.evento) === filtroEvento)
    : ordini;

  const testo = ricerca.trim().toLowerCase();
  const filtrati = perEvento.filter((o) => {
    if (filtroStato && o.stato !== filtroStato) return false;
    if (!testo) return true;
    return [o.codice, o.cliente_nome, o.cliente_telefono, o.evento_titolo]
      .some((v) => (v || '').toLowerCase().includes(testo));
  });

  const conta = (stato) => perEvento.filter((o) => o.stato === stato).length;
  const daIncassare = perEvento
    .filter((o) => o.stato === 'IN_SOSPESO' && !o.scaduto)
    .reduce((somma, o) => somma + Number(o.totale || 0), 0);
  const incassato = perEvento
    .filter((o) => o.stato === 'CONFERMATO')
    .reduce((somma, o) => somma + Number(o.totale || 0), 0);

  /* Gli ordini che stanno per scadere sono il motivo per cui questa
     pagina si apre: dopo, i posti tornano in vendita e il cliente che
     ha pagato resta a terra. */
  const inScadenza = perEvento.filter(
    (o) => o.stato === 'IN_SOSPESO' && !o.scaduto && o.ore_rimaste !== null && o.ore_rimaste <= 12
  );

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-gray-900">Ordini</h1>
          <p className="text-sm text-gray-500 mt-0.5">Biglietti venduti dal sito</p>
        </div>

        {inScadenza.length > 0 && (
          <div className="flex items-start gap-2.5 p-3 mb-5 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              {inScadenza.length === 1
                ? `L'ordine ${inScadenza[0].codice} scade tra ${inScadenza[0].ore_rimaste} ore.`
                : `${inScadenza.length} ordini scadono entro 12 ore.`}
              {' '}Se il pagamento è arrivato, confermalo; se è in viaggio, prorogalo.
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {['IN_SOSPESO', 'CONFERMATO'].map((stato) => {
            const Icona = STATI_ORDINE[stato].icona;
            return (
              <button
                key={stato}
                onClick={() => setFiltroStato(filtroStato === stato ? '' : stato)}
                className={`text-left bg-white rounded-xl border p-4 transition
                  ${filtroStato === stato ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200 hover:border-gray-400'}`}
              >
                <div className="flex items-center gap-2 text-gray-500 text-xs font-medium mb-1">
                  <Icona className="w-3.5 h-3.5" />
                  {STATI_ORDINE[stato].label}
                </div>
                <div className="text-2xl font-bold text-gray-900">{conta(stato)}</div>
              </button>
            );
          })}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs font-medium mb-1">
              <Clock className="w-3.5 h-3.5" />
              Da incassare
            </div>
            <div className="text-2xl font-bold text-amber-700">{euro(daIncassare)}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs font-medium mb-1">
              <Euro className="w-3.5 h-3.5" />
              Incassato
            </div>
            <div className="text-2xl font-bold text-gray-900">{euro(incassato)}</div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={ricerca}
              onChange={(e) => setRicerca(e.target.value)}
              placeholder="Cerca per codice, cliente, telefono o evento"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-900"
            />
          </div>
          {/* La tendina si vede appena c'e' un ordine, anche con un
              evento solo: nascosta finche' gli eventi sono due,
              sembrava che il filtro non ci fosse. */}
          {eventi.length > 0 && (
            <select
              value={filtroEvento}
              onChange={(e) => setFiltroEvento(e.target.value)}
              className={`sm:w-72 px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:border-gray-900
                ${filtroEvento ? 'border-gray-900 text-gray-900 font-medium' : 'border-gray-300 text-gray-700'}`}
            >
              <option value="">Tutti gli eventi</option>
              {eventi.map((ev) => (
                <option key={ev.id} value={String(ev.id)}>
                  {ev.titolo} · {dataIT(ev.data)}
                </option>
              ))}
            </select>
          )}
        </div>

        {caricamento ? (
          <div className="py-16 text-center text-gray-400">Carico…</div>
        ) : filtrati.length === 0 ? (
          <div className="py-16 text-center">
            <Ticket className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {ordini.length === 0 ? 'Nessun ordine ancora.' : 'Nessun ordine con questi filtri.'}
            </p>
            {ordini.length > 0 && (filtroEvento || filtroStato || testo) && (
              <button
                onClick={() => { setFiltroEvento(''); setFiltroStato(''); setRicerca(''); }}
                className="mt-2 text-sm font-medium text-gray-700 hover:text-gray-900 underline"
              >
                Togli i filtri
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
            {filtrati.map((o) => {
              const stato = STATI_ORDINE[o.stato] || STATI_ORDINE.IN_SOSPESO;
              const urgente = o.stato === 'IN_SOSPESO' && !o.scaduto
                && o.ore_rimaste !== null && o.ore_rimaste <= 12;
              return (
                <div
                  key={o.id}
                  onClick={() => setAperto(o)}
                  className="flex items-center gap-4 p-4 hover:bg-gray-50 cursor-pointer transition"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm text-gray-500">{o.codice}</span>
                      <span className="font-semibold text-gray-900 truncate">{o.cliente_nome}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${stato.classe}`}>
                        {o.scaduto && o.stato === 'IN_SOSPESO' ? 'Scaduto' : stato.label}
                      </span>
                      {urgente && (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-700">
                          Scade tra {o.ore_rimaste} h
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5 truncate">
                      {o.evento_titolo} · {dataIT(o.evento_data)} · {o.navetta_etichetta}
                      {o.fermata_luogo && ` · sale a ${o.fermata_luogo}`}
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <div className="font-semibold text-gray-900">{euro(o.totale)}</div>
                    <div className="text-xs text-gray-500">
                      {o.quantita} {o.quantita === 1 ? 'posto' : 'posti'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {aperto && (
        <PannelloOrdine
          ordineIniziale={aperto}
          modificabile={modificabile}
          onChiudi={() => setAperto(null)}
          onCambiato={() => { carica(); setAperto(null); }}
        />
      )}
    </AdminLayout>
  );
};

const PannelloOrdine = ({ ordineIniziale, modificabile, onChiudi, onCambiato }) => {
  const [ordine, setOrdine] = useState(null);
  const [inCorso, setInCorso] = useState(false);

  useEffect(() => {
    api.get(`/navette/ordini/${ordineIniziale.id}/`)
      .then((r) => setOrdine(r.data))
      .catch(() => notify.error('Non riesco a caricare l\'ordine'));
  }, [ordineIniziale.id]);

  const conferma = async () => {
    setInCorso(true);
    const idAvviso = notify.loading('Confermo l\'incasso…');
    try {
      const risposta = await api.post(`/navette/ordini/${ordine.id}/conferma/`);
      const esito = risposta.data.email;
      // La conferma vale anche se la email non parte: il pagamento
      // c'e' stato. Si dice com'e' andata invece di fingere.
      const andataMale = esito && /non e partita/.test(esito);
      notify.update(idAvviso, {
        type: andataMale ? 'warning' : 'success',
        render: andataMale
          ? 'Ordine confermato, ma i biglietti non sono partiti. Riprova con "Rimanda biglietti".'
          : 'Incasso confermato, biglietti inviati',
      });
      onCambiato();
    } catch (errore) {
      notify.update(idAvviso, { type: 'error', render: 'Conferma non riuscita' });
    } finally {
      setInCorso(false);
    }
  };

  const proroga = async () => {
    const ore = window.prompt('Di quante ore prorogare la scadenza?', '24');
    if (!ore) return;
    setInCorso(true);
    try {
      await api.post(`/navette/ordini/${ordine.id}/proroga/`, { ore: Number(ore) });
      notify.success(`Scadenza spostata avanti di ${ore} ore`);
      onCambiato();
    } catch (errore) {
      notify.error(errore.response?.data?.errore || 'Proroga non riuscita');
    } finally {
      setInCorso(false);
    }
  };

  const annulla = async () => {
    const motivo = window.prompt('Motivo dell\'annullamento (facoltativo):', '');
    if (motivo === null) return;
    setInCorso(true);
    try {
      await api.post(`/navette/ordini/${ordine.id}/annulla/`, { motivo });
      notify.success('Ordine annullato, i posti sono tornati in vendita');
      onCambiato();
    } catch (errore) {
      notify.error('Annullamento non riuscito');
    } finally {
      setInCorso(false);
    }
  };

  /* Buttare via l'ordine: le prove, il doppione di chi ha premuto due
     volte, lo scaduto di cui non resta niente da ricordare. Quello
     incassato no, e il server lo rifiuta comunque: i soldi presi sono
     un fatto, e per disfare la vendita c'e' "Annulla", che lascia
     scritto cos'e' successo. */
  const elimina = async () => {
    const posti = ordine.quantita === 1 ? 'il posto tornerà' : `i ${ordine.quantita} posti torneranno`;
    const avviso = ordine.stato === 'IN_SOSPESO' || ordine.stato === 'SCADUTO'
      ? ` Se era in attesa di pagamento, ${posti} in vendita.`
      : '';
    if (!window.confirm(
      `Elimino l'ordine ${ordine.codice} di ${ordine.cliente_dati?.nome_completo || ''}?`
      + ` Non ne resterà traccia.${avviso}`
    )) return;
    setInCorso(true);
    try {
      await api.delete(`/navette/ordini/${ordine.id}/`);
      notify.success(`Ordine ${ordine.codice} eliminato`);
      onCambiato();
    } catch (errore) {
      notify.error(errore.response?.data?.errore || 'Non riesco a eliminarlo');
    } finally {
      setInCorso(false);
    }
  };

  const rimanda = async () => {
    const indirizzo = window.prompt(
      'A quale indirizzo mandare i biglietti?', ordine.cliente_dati?.email || ''
    );
    if (!indirizzo) return;
    setInCorso(true);
    const idAvviso = notify.loading('Invio…');
    try {
      const risposta = await api.post(`/navette/ordini/${ordine.id}/reinvia_biglietti/`, {
        email: indirizzo,
      });
      notify.update(idAvviso, {
        type: 'success',
        render: `${risposta.data.inviati} biglietti inviati a ${risposta.data.email}`,
      });
    } catch (errore) {
      notify.update(idAvviso, {
        type: 'error',
        render: errore.response?.data?.errore || 'Invio non riuscito',
      });
    } finally {
      setInCorso(false);
    }
  };

  if (!ordine) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 text-gray-400">Carico…</div>
      </div>
    );
  }

  const stato = STATI_ORDINE[ordine.stato] || STATI_ORDINE.IN_SOSPESO;
  const cliente = ordine.cliente_dati || {};
  const inSospeso = ordine.stato === 'IN_SOSPESO';

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 sm:p-4"
      onClick={onChiudi}
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm text-gray-500">{ordine.codice}</span>
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${stato.classe}`}>
                {ordine.scaduto && inSospeso ? 'Scaduto' : stato.label}
              </span>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mt-1">{cliente.nome_completo}</h2>
          </div>
          <button onClick={onChiudi} className="p-1 text-gray-400 hover:text-gray-900" aria-label="Chiudi">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <dl className="text-sm">
            <Riga etichetta="Evento">
              {ordine.evento_titolo} · {dataIT(ordine.evento_data)}
            </Riga>
            <Riga etichetta="Partenza">{ordine.navetta_etichetta}</Riga>
            <Riga etichetta="Sale a">
              {ordine.fermata_luogo
                ? ordine.fermata_luogo + (ordine.fermata_ora ? ` · ${ordine.fermata_ora.slice(0, 5)}` : '')
                : <span className="text-amber-700">da chiedere al cliente</span>}
            </Riga>
            <Riga etichetta="Posti">{ordine.quantita}</Riga>
            <Riga etichetta="Prezzo a persona">{euro(ordine.prezzo_unitario)}</Riga>
            <Riga etichetta="Totale">
              <strong className="text-gray-900">{euro(ordine.totale)}</strong>
            </Riga>
            <Riga etichetta="Pagamento">{ordine.metodo_nome || 'non indicato'}</Riga>
            {inSospeso && (
              <Riga etichetta="Scade">
                {dataOraIT(ordine.scade_il)}
                {ordine.ore_rimaste !== null && !ordine.scaduto && (
                  <span className={ordine.ore_rimaste <= 12 ? 'text-red-600 font-semibold ml-1.5' : 'text-gray-400 ml-1.5'}>
                    (tra {ordine.ore_rimaste} h)
                  </span>
                )}
              </Riga>
            )}
            {ordine.confermato_il && (
              <Riga etichetta="Incassato il">{dataOraIT(ordine.confermato_il)}</Riga>
            )}
            {ordine.motivo_annullamento && (
              <Riga etichetta="Motivo">{ordine.motivo_annullamento}</Riga>
            )}
          </dl>

          <div className="rounded-lg bg-gray-50 p-3.5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Il cliente
            </h3>
            <div className="space-y-1.5 text-sm">
              <a href={`tel:${cliente.telefono}`} className="flex items-center gap-2 text-gray-900 hover:text-blue-700">
                <Phone className="w-3.5 h-3.5 text-gray-400" />
                {cliente.telefono || '—'}
              </a>
              <a href={`mailto:${cliente.email}`} className="flex items-center gap-2 text-gray-900 hover:text-blue-700 break-all">
                <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                {cliente.email || '—'}
              </a>
            </div>
          </div>

          {ordine.biglietti?.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Biglietti
              </h3>
              <div className="space-y-1.5">
                {ordine.biglietti.map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-3 text-sm py-1.5 border-b border-gray-100 last:border-0">
                    <span className="font-mono text-xs text-gray-500 shrink-0">{b.codice}</span>
                    <span className="min-w-0 flex-1 truncate text-gray-900">
                      {b.nome_completo || <span className="text-gray-400">non nominativo</span>}
                    </span>
                    <span className="shrink-0 text-xs">
                      {b.usato_il
                        ? <span className="text-gray-400">salito</span>
                        : b.valido
                          ? <span className="text-green-700">valido</span>
                          : <span className="text-gray-400">non attivo</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {modificabile && (
          <div className="p-5 border-t border-gray-100 flex flex-wrap items-center gap-2 sticky bottom-0 bg-white">
            {inSospeso && (
              <button
                onClick={conferma}
                disabled={inCorso}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium text-sm disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" />
                Ho incassato
              </button>
            )}
            {(inSospeso || ordine.stato === 'SCADUTO') && (
              <button
                onClick={proroga}
                disabled={inCorso}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:border-gray-900 transition font-medium text-sm disabled:opacity-50"
              >
                <CalendarPlus className="w-4 h-4" />
                Proroga
              </button>
            )}
            {ordine.stato === 'CONFERMATO' && (
              <button
                onClick={rimanda}
                disabled={inCorso}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:border-gray-900 transition font-medium text-sm disabled:opacity-50"
              >
                <Mail className="w-4 h-4" />
                Rimanda biglietti
              </button>
            )}
            <div className="flex items-center gap-2 ml-auto">
              {ordine.stato !== 'ANNULLATO' && (
                <button
                  onClick={annulla}
                  disabled={inCorso}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:border-red-600 hover:text-red-600 transition font-medium text-sm disabled:opacity-50"
                >
                  <Ban className="w-4 h-4" />
                  Annulla
                </button>
              )}
              {ordine.stato !== 'CONFERMATO' && (
                <button
                  onClick={elimina}
                  disabled={inCorso}
                  title="Toglie l'ordine dal gestionale, senza lasciare traccia"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-700 transition font-medium text-sm disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Elimina
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Riga = ({ etichetta, children }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
    <dt className="text-gray-500 shrink-0">{etichetta}</dt>
    <dd className="text-right text-gray-900 min-w-0">{children}</dd>
  </div>
);

export default OrdiniEventi;
